import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TenantEnquiryType } from '@/lib/enquiry-types';

export interface ContactCompanyInfo {
  company_name: string;
  phone_number: string | null;
  service_areas: string[] | null;
  footer_tagline: string | null;
}

export interface ContactBusinessHour {
  day_of_week: number;
  is_open: boolean;
  open_time: string;
  close_time: string;
}

/**
 * Company info for the Contact page (phone, service areas, tagline).
 */
export function useContactCompanyInfo(orgId: string | undefined) {
  return useQuery({
    queryKey: ['contact-company-info', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<ContactCompanyInfo | null> => {
      const { data, error } = await supabase
        .from('company_info')
        .select('company_name, phone_number, service_areas, footer_tagline')
        .eq('organization_id', orgId!)
        .maybeSingle();

      if (error) throw error;
      return data ?? null;
    },
  });
}

/**
 * Business hours for the Contact page sidebar, ordered by day of week.
 */
export function useContactBusinessHours(orgId: string | undefined) {
  return useQuery({
    queryKey: ['contact-business-hours', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<ContactBusinessHour[]> => {
      const { data, error } = await supabase
        .from('business_hours')
        .select('day_of_week, is_open, open_time, close_time')
        .eq('organization_id', orgId!)
        .order('day_of_week');

      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface ContactFormInput {
  orgId: string;
  name: string;
  email: string;
  phone: string;
  /** What the message is about (see src/lib/enquiry-types). */
  enquiryType: TenantEnquiryType;
  message: string;
}

/**
 * Submits the Contact form as a callback_request row and fires confirmation /
 * owner-notification emails (non-blocking — a failed email must not fail the
 * submission).
 */
export function useSubmitContactForm() {
  return useMutation({
    mutationFn: async (input: ContactFormInput): Promise<void> => {
      // Generate the id client-side so we don't need the inserted row back.
      // Public anon RLS allows INSERT but not SELECT on callback_requests, so
      // an INSERT ... RETURNING (`.select('id').single()`) is rejected under
      // live RLS. A plain insert with a known id avoids the failed read.
      const recordId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
              (
                Number(c) ^
                ((Math.random() * 16) >> (Number(c) / 4))
              ).toString(16),
            );

      const { error } = await supabase.from('callback_requests').insert({
        id: recordId,
        organization_id: input.orgId,
        name: input.name,
        email: input.email,
        phone: input.phone || 'Not provided',
        preferred_time: 'Contact form submission',
        notes: input.message,
        enquiry_type: input.enquiryType,
      });

      if (error) throw error;

      try {
        await supabase.functions.invoke('send-enquiry-confirmation', {
          body: {
            recordId,
            source: 'callback',
            organizationId: input.orgId,
          },
        });
      } catch (emailError) {
        console.error('Email notification failed:', emailError);
      }

      try {
        await supabase.functions.invoke('send-owner-notification', {
          body: {
            recordId,
            source: 'callback',
            organizationId: input.orgId,
          },
        });
      } catch (notifyError) {
        console.error('Owner notification failed:', notifyError);
      }
    },
  });
}
